// The template editor: one form for the whole template, and a preview that
// renders the form's live values beside it.
//
// Why one form. A template is what every screen using it looks like AND what
// it says, so saving publishes to the floor. A preview that only shows saved
// state therefore arrives too late to be a preview at all — and the artwork
// slots used to make that worse by saving themselves the moment an image was
// picked. Now every field, images included, feeds one draft; the iframe
// follows it keystroke by keystroke, and Save is the single moment anything
// reaches a live screen.
//
// Creating and editing share the whole of that. The preview runs off form
// state rather than a saved row, so it works before anything exists — which is
// why a new template gets this same page instead of a dialog asking for a name.
// The two differ only in where Save sends the body and what happens after, so
// they are two thin wrappers over one form.

import { SaveBar, toast } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@/api/error";
import { useCreateTemplate, usePatchTemplate } from "@/api/templates";
import type { Template, TemplateBody } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { useFontUrl } from "@/features/assets/asset-queries";
import { resolveArtworkUrls } from "@/features/assets/resolve-artwork-urls";
import { PreviewPanel } from "@/features/preview/preview-panel";
import { applyProblemDetails, Form, firstFieldMessage, useKoreForm } from "@/lib/forms";
import { friendlyMessage } from "@/lib/friendly-error";
import { SAVE_BAR_IN_FLOW, useSaveBar } from "@/lib/save-bar";
import { ContentSection } from "./section-list";
import {
    NEW_TEMPLATE_DEFAULTS,
    OWN_FONT,
    type TemplateValues,
    templateSchema,
    toBody,
    toDefaults,
    toPreviewTemplate,
} from "./template-schema";
import { BackgroundSection, CssSection, NameSection, TypeSection } from "./template-sections";

interface EditorFormProps {
    mode: "create" | "edit";
    defaultValues: TemplateValues;
    /** Persists the body. Resolves with whatever the server stored. */
    save: (body: TemplateBody) => Promise<Template>;
    /** Runs after a successful save, with the stored template. */
    saved: (template: Template, form: ReturnType<typeof useKoreForm<typeof templateSchema>>) => void;
    isPending: boolean;
    /** Create only: where Cancel goes. */
    onCancel: () => void;
}

function EditorForm({ mode, defaultValues, save, saved, isPending, onCancel }: EditorFormProps) {
    const form = useKoreForm({ schema: templateSchema, defaultValues });
    // The whole point of the page: re-render on every keystroke so the draft
    // handed to the preview is always the current one.
    const values = form.watch();
    const isCreate = mode === "create";
    // Shared with the preview and the picker, so a manifest the preview has
    // already fetched costs nothing to read again at submit.
    const assetCtx = useAssetClientContext();
    const queryClient = useQueryClient();
    // The Your font card draws the uploaded font itself, from the same
    // manifest cache the preview and the picker read.
    const ownFontUrl = useFontUrl(assetCtx, values.fontChoice === OWN_FONT ? values.fontAssetId || null : null);
    // A new template is unsaved by definition, so the bar arms from the first
    // paint: defaults that are all acceptable are still worth creating.
    const saveBar = useSaveBar({
        dirty: isCreate || form.formState.isDirty,
        saving: isPending,
        invalid: Object.keys(form.formState.errors).length > 0,
    });

    async function onSubmit(submitted: TemplateValues) {
        saveBar.submitting();
        try {
            // Artwork is stored as a public render URL beside its asset id,
            // because the display endpoint is public and cannot read a delivery
            // manifest itself. Resolved here rather than taken from the preview:
            // the preview's URL can lag the form by one fetch, and writing a
            // stale one would put the previous asset's artwork under the new
            // asset's id. `dirtyFields` is what tells the resolver whether a
            // failure may leave the stored URL alone or must clear it — a rule
            // that only applies to `backgroundImage`'s column; every image in
            // `sections` resolves on every save, changed or not.
            const body = toBody(submitted);
            const artwork = await resolveArtworkUrls(assetCtx, queryClient, {
                backgroundImage: {
                    assetId: submitted.backgroundImageAssetId || null,
                    changed: Boolean(form.formState.dirtyFields.backgroundImageAssetId),
                },
                // Choosing a bundled card also moves the reference (to none), so
                // a change of either field is a change of font.
                font: {
                    assetId: body.fontAssetId ?? null,
                    changed: Boolean(form.formState.dirtyFields.fontAssetId || form.formState.dirtyFields.fontChoice),
                },
                sections: body.sections ?? [],
            });
            saved(await save({ ...body, ...artwork }), form);
            saveBar.saved();
        } catch (err) {
            if (err instanceof ApiError) {
                // As on the screen form: violations on their fields, the rest
                // in a toast.
                const { onFields, unmapped } = applyProblemDetails(form, err);
                const fallback = `Failed to ${isCreate ? "create" : "save"} template`;
                if (onFields) {
                    saveBar.failed(friendlyMessage(err));
                    return;
                }
                toastMutationError(isCreate ? "Create failed" : "Save failed", err, { unmapped });
                saveBar.failed(fallback);
                return;
            }
            throw err;
        }
    }

    return (
        <Form form={form} onSubmit={onSubmit} className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
            <div className="flex min-w-0 flex-col gap-6">
                <NameSection control={form.control} />
                <BackgroundSection control={form.control} />
                <ContentSection control={form.control} />
                <TypeSection control={form.control} fontChoice={String(values.fontChoice)} ownFontUrl={ownFontUrl} />
                <CssSection control={form.control} />
                {/* kore's SaveBar, like every other save on the admin: one
                    paradigm beats three. It replaced a hand-rolled card at the
                    foot of this column — and it sits where that card sat, in
                    flow, because the hand-rolled one was right about this
                    editor: a bar pinned to the viewport floats over 40rem of
                    monospace. `SAVE_BAR_IN_FLOW` is that decision, shared with
                    the screen and location forms. */}
                <SaveBar
                    className={SAVE_BAR_IN_FLOW}
                    state={saveBar.state}
                    errorMessage={saveBar.errorMessage}
                    saveLabel={isCreate ? "Create template" : "Save template"}
                    dirtySaveLabel={isCreate ? "Create template" : "Save template"}
                    // Create has nothing to discard — the same button leaves
                    // without creating anything.
                    discardLabel={isCreate ? "Cancel" : "Discard"}
                    statusLabels={{
                        clean: "All changes saved.",
                        saved: () => "All changes saved.",
                        // Worth spelling out on this page specifically: saving a
                        // template changes every screen already using it.
                        dirty: () =>
                            isCreate
                                ? "New template · not yet saved"
                                : "Unsaved changes — live screens still show the saved version.",
                    }}
                    onSave={() =>
                        void form.handleSubmit(onSubmit, (errors) => saveBar.blocked(firstFieldMessage(errors)))()
                    }
                    retryLabel={isCreate ? "Create template" : "Save template"}
                    onDiscard={isCreate ? onCancel : () => form.reset(defaultValues)}
                />
            </div>
            {/* Scrolls inside itself: a portrait preview is taller than the
                viewport, and a sticky column that clips has no way to reach
                the bottom of it. */}
            <aside className="min-w-0 xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto">
                <PreviewPanel
                    template={toPreviewTemplate(values as TemplateValues)}
                    width={values.previewWidth}
                    height={values.previewHeight}
                    // The panel's own size control (presets, free entry, the
                    // drag handle) writes straight into these two form
                    // fields — not panel-local state — so a resize persists
                    // with the template through the one SaveBar below,
                    // never a second save path of its own.
                    onSizeChange={({ width, height }) => {
                        form.setValue("previewWidth", Math.max(1, Math.round(width)), { shouldDirty: true });
                        form.setValue("previewHeight", Math.max(1, Math.round(height)), { shouldDirty: true });
                    }}
                />
            </aside>
        </Form>
    );
}

export interface TemplateEditorProps {
    template: Template;
}

export function TemplateEditor({ template }: TemplateEditorProps) {
    const patchTemplate = usePatchTemplate(template.workspaceId, template.id);
    return (
        <EditorForm
            mode="edit"
            defaultValues={toDefaults(template)}
            save={(body) => patchTemplate.mutateAsync(body)}
            saved={(updated, form) => {
                form.reset(toDefaults(updated));
                toast.success("Template saved");
            }}
            isPending={patchTemplate.isPending}
            // Edit offers Discard instead; nothing to cancel out of.
            onCancel={() => undefined}
        />
    );
}

export interface TemplateCreateEditorProps {
    workspaceId: string;
}

export function TemplateCreateEditor({ workspaceId }: TemplateCreateEditorProps) {
    const createTemplate = useCreateTemplate(workspaceId);
    const navigate = useNavigate();
    return (
        <EditorForm
            mode="create"
            defaultValues={NEW_TEMPLATE_DEFAULTS}
            save={(body) => createTemplate.mutateAsync(body)}
            saved={(created) => {
                toast.success("Template created");
                navigate({
                    to: "/w/$workspaceId/templates/$templateId",
                    params: { workspaceId, templateId: created.id },
                });
            }}
            isPending={createTemplate.isPending}
            onCancel={() => navigate({ to: "/w/$workspaceId/templates", params: { workspaceId } })}
        />
    );
}
